import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  Battery,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FolderKanban,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search,
  Sparkles,
  Sun,
  Wind,
  X as XIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import epcProjectService, {
  type EpcProject,
  type EpcProjectHealth,
  type EpcProjectMembershipRow,
} from '../../../services/epcProjectService';
import EpcProjectsHeroArt from './EpcProjectsHeroArt';
import EpcProjectCard from '../project/card';
import EpcProjectFilterBar from '../project/EpcProjectFilterBar';
import EpcProjectListRow from '../project/EpcProjectListRow';

import { deriveProjectProgress } from '../project/card/epcProjectProgress';
import { EPC_THEME_STYLES, SOVEREIGN, type EpcVisualTheme } from '../utils/epcProjectVisual';

const PAGE_SIZE = 20;

const THEME_ICON: Record<EpcVisualTheme, typeof Sun> = {
  solar: Sun,
  wind: Wind,
  hybrid: Sparkles,
  bess: Battery,
  generic: FolderKanban,
};

type CategoryFilterValue = '' | 'solar' | 'wind' | 'hybrid' | 'bess';

const CATEGORY_TABS: { value: CategoryFilterValue; label: string; theme: EpcVisualTheme | null }[] = [
  { value: '',       label: 'All categories', theme: null },
  { value: 'solar',  label: 'Solar',          theme: 'solar' },
  { value: 'wind',   label: 'Wind',           theme: 'wind' },
  { value: 'hybrid', label: 'Hybrid',         theme: 'hybrid' },
  { value: 'bess',   label: 'BESS',           theme: 'bess' },
];

type HealthFilterValue = '' | EpcProjectHealth;

interface HealthChip {
  value: HealthFilterValue;
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  ink: string;
  bg: string;
}

const HEALTH_CHIPS: HealthChip[] = [
  { value: '',          label: 'All status', icon: Sparkles,     ink: '#1a3a00', bg: '#A6CE39' },
  { value: 'on_track',  label: 'On track',   icon: CheckCircle2, ink: '#047857', bg: 'rgba(16,185,129,0.10)' },
  { value: 'at_risk',   label: 'At risk',    icon: AlertTriangle, ink: '#8A5A08', bg: 'rgba(201,169,97,0.12)' },
  { value: 'delayed',   label: 'Delayed',    icon: Activity,     ink: '#B91C1C', bg: 'rgba(239,68,68,0.10)' },
  { value: 'completed', label: 'Completed',  icon: CheckCircle2, ink: '#0369A1', bg: 'rgba(14,165,233,0.10)' },
  { value: 'upcoming',  label: 'Upcoming',   icon: Clock,        ink: '#475569', bg: 'rgba(148,163,184,0.10)' },
];

type SortKey = 'recent' | 'name' | 'capacity' | 'health';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recent',   label: 'Most recent' },
  { value: 'name',     label: 'Name (A → Z)' },
  { value: 'capacity', label: 'Capacity (high → low)' },
  { value: 'health',   label: 'Health (concerning first)' },
];

type ViewMode = 'grid' | 'list';

const HEALTH_RANK: Record<EpcProjectHealth, number> = {
  delayed: 0,
  at_risk: 1,
  not_configured: 2,
  upcoming: 3,
  on_track: 4,
  completed: 5,
};

function refName(ref: unknown): string {
  if (ref && typeof ref === 'object' && 'name' in ref) {
    return String((ref as { name?: string }).name ?? '');
  }
  return '';
}

function capacityOf(p: EpcProject): number {
  const ac = typeof p.acCapacityMw === 'number' ? p.acCapacityMw : NaN;
  if (Number.isFinite(ac) && ac > 0) return ac;
  const dc = typeof p.dcCapacityMw === 'number' ? p.dcCapacityMw : NaN;
  if (Number.isFinite(dc) && dc > 0) return dc;
  return 0;
}

export default function EpcProjectListPage() {
  const { user: authUser, hasPermission } = useAuth();
  const canCreate = hasPermission('epcProject', 'create');
  const canEdit = hasPermission('epcProject', 'update');
  const currentUserId = useMemo(() => {
    const u = authUser as { _id?: string; id?: string } | null;
    return u?._id ? String(u._id) : u?.id ? String(u.id) : undefined;
  }, [authUser]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilterValue>('');
  const [locationId, setLocationId] = useState('');
  const [healthFilter, setHealthFilter] = useState<HealthFilterValue>('');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Strip stale `?mine` query params from prior versions of this page.
  useEffect(() => {
    if (!searchParams.has('mine')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('mine');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // ── Search debounce ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  // ── Keyboard shortcut: `/` focuses the search ────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        const tag = (document.activeElement as HTMLElement | null)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Project list (TanStack Query — caches per filter/page combination) ──
  //
  // Project scoping note (R5):
  //   The list returned by `epcProjectService.list()` is already correctly
  //   scoped on the backend. Non-SuperAdmin callers receive only projects
  //   where they hold an active EpcProjectMembership — see
  //   `epcProject.service.ts > queryEpcProjects()` (membership filter at
  //   `_id IN memberProjectIds`) and the `epcProjectMembershipFallback`
  //   auth guard on `GET /api/v1/epc/projects`.
  //   Governance roles (EPC Head / PMO / Project Coordinator) are added as
  //   project members on the projects they manage, so they continue to see
  //   their portfolio. Only SuperAdmin bypasses the membership scope.
  //   No additional client-side filtering is required here.
  const { data: projectsData, isLoading: loading, isError: projectsError, error: projectsErr } = useQuery({
    queryKey: ['epc-projects', page, categoryFilter, locationId, searchDebounced],
    queryFn: () =>
      epcProjectService.list({
        page,
        limit: PAGE_SIZE,
        ...(categoryFilter ? { projectTypeCategory: categoryFilter } : {}),
        ...(locationId ? { locationId } : {}),
        ...(searchDebounced ? { search: searchDebounced } : {}),
      }),
    staleTime: 30_000,
  });

  const items = projectsData?.data ?? [];
  const totalPages = Math.max(1, projectsData?.meta?.totalPages ?? 1);
  const total = projectsData?.meta?.totalDocs ?? items.length;

  useEffect(() => {
    if (projectsError) {
      const msg = projectsErr instanceof Error ? projectsErr.message : 'Could not load EPC projects';
      toast.error(msg);
    }
  }, [projectsError, projectsErr]);

  // Reset to page 1 whenever the search term changes.
  useEffect(() => { setPage(1); }, [searchDebounced]);

  // ── Membership fetch: single call instead of one-per-project (N+1 fix) ──
  const { data: myMembershipsRaw = [] } = useQuery({
    queryKey: ['epc-my-memberships'],
    queryFn: () => epcProjectService.getMyMemberships(),
    staleTime: 5 * 60_000,
  });

  const myMembershipByProject = useMemo(() => {
    const map = new Map<string, EpcProjectMembershipRow>();
    for (const m of myMembershipsRaw) {
      map.set(m.epcProjectId, {
        id: m.epcProjectId,
        userId: currentUserId ?? '',
        role: m.role,
        orgUnitId: m.orgUnitId,
        orgUnitName: m.orgUnitName,
        inactive: false,
      });
    }
    return map;
  }, [myMembershipsRaw, currentUserId]);

  // ── Visible items: health filter + sort ──────────────────────────────────
  const visibleItems = useMemo(() => {
    let list = items;
    if (healthFilter) {
      list = list.filter((p) => deriveProjectProgress({ project: p }).health === healthFilter);
    }
    const sorted = [...list];
    switch (sortKey) {
      case 'name':
        sorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        break;
      case 'capacity':
        sorted.sort((a, b) => capacityOf(b) - capacityOf(a));
        break;
      case 'health':
        sorted.sort((a, b) => {
          const ha = deriveProjectProgress({ project: a }).health;
          const hb = deriveProjectProgress({ project: b }).health;
          return HEALTH_RANK[ha] - HEALTH_RANK[hb];
        });
        break;
      case 'recent':
      default:
        sorted.sort((a, b) => {
          const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return tb - ta;
        });
        break;
    }
    return sorted;
  }, [items, healthFilter, sortKey]);

  // ── Health counts per chip (from current page items, before health filter) ─
  const healthCounts = useMemo(() => {
    const counts: Partial<Record<HealthFilterValue, number>> = { '': items.length };
    for (const p of items) {
      const h = deriveProjectProgress({ project: p }).health as HealthFilterValue;
      counts[h] = (counts[h] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  // ── Active filter chips (descriptive list of what's applied) ─────────────
  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (categoryFilter) {
      const t = CATEGORY_TABS.find((c) => c.value === categoryFilter);
      chips.push({
        key: 'cat',
        label: `Category: ${t?.label ?? categoryFilter}`,
        clear: () => { setCategoryFilter(''); setPage(1); },
      });
    }
    if (healthFilter) {
      const h = HEALTH_CHIPS.find((c) => c.value === healthFilter);
      chips.push({
        key: 'health',
        label: `Status: ${h?.label ?? healthFilter}`,
        clear: () => setHealthFilter(''),
      });
    }
    if (locationId) {
      chips.push({ key: 'loc', label: 'Location set', clear: () => setLocationId('') });
    }
    if (searchDebounced) {
      chips.push({ key: 'q', label: `Search: "${searchDebounced}"`, clear: () => setSearch('') });
    }
    return chips;
  }, [categoryFilter, healthFilter, locationId, searchDebounced]);

  const clearAllFilters = () => {
    setCategoryFilter('');
    setHealthFilter('');
    setLocationId('');
    setSearch('');
    setPage(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="min-h-full font-[family-name:var(--font-outfit)]"
    >
      <div className="mx-auto w-full max-w-[1600px] min-w-0 px-3 sm:px-5 md:px-6 py-4 sm:py-5 space-y-5">

        {/* ════════ HERO BANNER ════════ */}
        <section
          className="relative overflow-hidden rounded-3xl border border-emerald-200/40 shadow-[0_8px_28px_-12px_rgba(0,99,70,0.28)]"
          style={{
            background:
              'linear-gradient(135deg, #006346 0%, #0a7a55 34%, #1a9468 66%, #2aaa82 100%)',
            fontFeatureSettings: '"tnum", "ss01"',
          }}
        >
          {/* Lime glow accent (top-right) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full blur-3xl"
            style={{ background: 'rgba(166,206,57,0.22)' }}
          />

          {/* Engraved wind + solar scene */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-[88%] sm:w-[64%] opacity-60 md:opacity-95">
            <EpcProjectsHeroArt />
          </div>

          {/* Hairline frame */}
          <div aria-hidden className="pointer-events-none absolute inset-2 rounded-[20px] border border-white/15" />
          <div aria-hidden className="pointer-events-none absolute inset-3.5 rounded-2xl border border-dashed border-white/10" />

          {/* Bottom hairline */}
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          <motion.div
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } }}
            className="relative px-6 py-8 sm:px-8 sm:py-9 lg:px-10 flex min-h-[176px] flex-col gap-5 sm:flex-row sm:items-end sm:justify-between"
          >
            <div className="min-w-0 flex-1">
              <motion.h1
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                className="text-[28px] sm:text-[32px] font-bold leading-[1.1] text-white tracking-[-0.015em]"
              >
                EPC Projects
              </motion.h1>
              <motion.p
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                className="mt-1.5 text-[13px] text-emerald-50/90 max-w-2xl"
              >
                Solar, wind, hybrid and BESS — schedules, imports, and team workspaces in one place.
                Press <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded bg-white/15 border border-white/20 text-[10px] font-mono">/</kbd> to search.
              </motion.p>
            </div>

            <motion.div
              variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
              className="flex flex-wrap items-center gap-2"
            >
              {!loading && total > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm px-3 py-1.5 text-[11px] font-semibold text-emerald-50 border border-white/15">
                  <span aria-hidden className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#A6CE39] opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#A6CE39]" />
                  </span>
                  <span className="tabular-nums">{total}</span> active project{total === 1 ? '' : 's'}
                </span>
              )}
              {canCreate && (
                <Link
                  to="/epc/projects/new"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-emerald-900 shadow-[0_4px_14px_-4px_rgba(15,23,42,0.25)] transition hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.35)] hover:-translate-y-0.5"
                >
                  <Plus className="h-4 w-4" strokeWidth={2.4} />
                  New project
                </Link>
              )}
            </motion.div>
          </motion.div>
        </section>

        {/* ════════ STICKY TOOLBAR ════════ */}
        <section
          className="sticky top-2 z-20 rounded-2xl overflow-hidden shadow-[0_2px_14px_-4px_rgba(0,99,70,0.12)]"
          style={{
            background: 'linear-gradient(135deg, #F0FAF5 0%, #ffffff 60%, #F5F3EF 100%)',
            border: '1px solid #e4dfd6',
          }}
        >
          <div className="p-3.5 space-y-3">
            {/* Row 1: search + sort + view */}
            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
                  style={{ color: '#9a9488' }}
                  strokeWidth={2}
                />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or code…"
                  className="w-full rounded-xl pl-10 pr-16 py-2.5 text-[13px] outline-none transition"
                  style={{
                    background: '#F5F3EF',
                    border: '1px solid #e4dfd6',
                    color: '#111827',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#006346';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(166,206,57,0.22)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e4dfd6';
                    e.currentTarget.style.boxShadow = '';
                  }}
                />
                <kbd
                  className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded text-[10px] font-mono"
                  style={{ border: '1px solid #e4dfd6', background: '#F5F3EF', color: '#9a9488' }}
                >
                  /
                </kbd>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-[11px] font-bold uppercase tracking-[0.10em]" style={{ color: '#374151' }}>
                  Sort
                </label>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                  className="rounded-lg px-2.5 py-1.5 text-[12px] font-medium outline-none transition"
                  style={{
                    background: '#F5F3EF',
                    border: '1px solid #e4dfd6',
                    color: '#111827',
                  }}
                >
                  {SORT_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>

                <div
                  role="group"
                  aria-label="View mode"
                  className="inline-flex items-center rounded-lg p-0.5"
                  style={{ border: '1px solid #e4dfd6', background: '#F5F3EF' }}
                >
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    aria-pressed={viewMode === 'grid'}
                    title="Card grid"
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md transition"
                    style={viewMode === 'grid'
                      ? { background: '#006346', color: '#fff' }
                      : { color: '#374151' }}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" strokeWidth={2.4} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    aria-pressed={viewMode === 'list'}
                    title="Compact list"
                    className="inline-flex items-center justify-center h-7 w-7 rounded-md transition"
                    style={viewMode === 'list'
                      ? { background: '#006346', color: '#fff' }
                      : { color: '#374151' }}
                  >
                    <ListIcon className="h-3.5 w-3.5" strokeWidth={2.4} />
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: category + health chips */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Category tabs */}
              <div className="inline-flex items-center gap-1.5 flex-wrap">
                {CATEGORY_TABS.map((tab) => {
                  const active = categoryFilter === tab.value;
                  const styles = tab.theme ? EPC_THEME_STYLES[tab.theme] : null;
                  const Icon = tab.theme ? THEME_ICON[tab.theme] : FolderKanban;
                  const activeBg    = styles?.accent ?? '#006346';
                  const activeLabel = styles?.accent ? 'rgba(255,255,255,0.78)' : '#A6CE39';
                  return (
                    <button
                      key={tab.value || 'all'}
                      type="button"
                      onClick={() => { setCategoryFilter(tab.value); setPage(1); }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-[7px] transition"
                      style={active ? {
                        background: activeBg,
                        border: `1px solid ${activeBg}`,
                        boxShadow: `0 2px 8px -2px ${activeBg}55`,
                      } : {
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                      }}
                    >
                      <Icon
                        className="h-3 w-3 shrink-0"
                        strokeWidth={2.4}
                        style={{ color: active ? activeLabel : '#6b7280' }}
                      />
                      <span
                        className="text-[11px] font-bold uppercase tracking-[0.07em]"
                        style={{ color: active ? '#ffffff' : '#374151' }}
                      >
                        {tab.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <span aria-hidden className="hidden sm:inline-block h-5 w-px" style={{ background: '#e4dfd6' }} />

              {/* Health quick filter */}
              <div className="inline-flex items-center gap-1.5 flex-wrap">
                {HEALTH_CHIPS.map((chip) => {
                  const active = healthFilter === chip.value;
                  const count  = healthCounts[chip.value] ?? 0;
                  // "All status" active = lime pill; specific statuses = solid ink fill
                  const activeBg    = chip.value === '' ? '#A6CE39' : chip.ink;
                  const activeNum   = chip.value === '' ? '#1a3a00' : '#ffffff';
                  const activeLbl   = chip.value === '' ? 'rgba(26,58,0,0.72)' : 'rgba(255,255,255,0.78)';
                  return (
                    <button
                      key={chip.value || 'all-health'}
                      type="button"
                      onClick={() => setHealthFilter(chip.value)}
                      className="inline-flex items-center gap-[5px] rounded-full px-3 py-[7px] transition"
                      style={active ? {
                        background: activeBg,
                        border: `1px solid ${activeBg}`,
                        boxShadow: `0 2px 8px -2px ${activeBg}55`,
                      } : {
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                      }}
                    >
                      <span
                        className="text-[13px] font-black tabular-nums leading-none"
                        style={{ color: active ? activeNum : '#111827' }}
                      >
                        {count}
                      </span>
                      <span
                        className="text-[9px] font-bold uppercase tracking-[0.10em] leading-none"
                        style={{ color: active ? activeLbl : '#6b7280' }}
                      >
                        {chip.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 3: location filter */}
            <EpcProjectFilterBar locationId={locationId} setLocationId={setLocationId} setPage={setPage} />

            {/* Active filter summary */}
            {activeFilterChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1" style={{ borderTop: '1px solid #e4dfd6' }}>
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.12em] mr-0.5"
                  style={{ color: '#9a9488' }}
                >
                  Active:
                </span>
                {activeFilterChips.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold"
                    style={{
                      background: '#F5F3EF',
                      border: '1px solid #e4dfd6',
                      color: '#006346',
                    }}
                  >
                    {c.label}
                    <button
                      type="button"
                      onClick={c.clear}
                      className="inline-flex items-center justify-center h-3.5 w-3.5 rounded transition"
                      style={{ color: '#006346' }}
                      aria-label="Clear filter"
                    >
                      <XIcon className="h-2.5 w-2.5" strokeWidth={2.6} />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="ml-auto text-[10.5px] font-semibold underline-offset-2 hover:underline"
                  style={{ color: '#006346' }}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ════════ CONTENT ════════ */}
        <section
          className="rounded-2xl overflow-hidden shadow-[0_1px_4px_rgba(11,59,46,0.08)]"
          style={{ background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}` }}
        >
          <div className="p-4 sm:p-5">
            {loading ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-[240px] animate-pulse rounded-2xl"
                      style={{ border: `1px solid ${SOVEREIGN.cardBorder}`, background: 'rgba(226,213,191,0.45)' }}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-[68px] animate-pulse rounded-xl"
                      style={{ border: `1px solid ${SOVEREIGN.cardBorder}`, background: 'rgba(226,213,191,0.45)' }}
                    />
                  ))}
                </div>
              )
            ) : visibleItems.length === 0 ? (
              <EmptyState
                hasItems={items.length > 0}
                onClearAll={clearAllFilters}
                canCreate={canCreate}
              />
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visibleItems.map((p) => (
                  <EpcProjectCard
                    key={p._id}
                    project={p}
                    canEdit={canEdit}
                    myMembership={myMembershipByProject.get(p._id) ?? undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {/* Column header (md+) */}
                <div
                  className="hidden md:grid grid-cols-12 gap-3 px-3.5 pb-1 text-[10px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: SOVEREIGN.kpiCaption }}
                >
                  <div className="col-span-5">Project</div>
                  <div className="col-span-2">Capacity</div>
                  <div className="col-span-2">Progress</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {visibleItems.map((p, i) => (
                  <EpcProjectListRow
                    key={p._id}
                    project={p}
                    canEdit={canEdit}
                    myMembership={myMembershipByProject.get(p._id) ?? undefined}
                    index={i}
                  />
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}`, background: 'rgba(242,234,219,0.40)' }}
            >
              <p className="text-[11.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
                Page{' '}
                <span className="font-semibold tabular-nums" style={{ color: SOVEREIGN.title }}>{page}</span>
                {' '}of{' '}
                <span className="font-semibold tabular-nums" style={{ color: SOVEREIGN.title }}>{totalPages}</span>
                {total ? <span style={{ color: SOVEREIGN.kpiCaption }}> · {total} total</span> : null}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((n) => Math.max(1, n - 1))}
                  className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11.5px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: SOVEREIGN.inputBg,
                    border: `1px solid ${SOVEREIGN.inputBorder}`,
                    color: SOVEREIGN.title,
                  }}
                >
                  <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.4} />
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                  className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11.5px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: SOVEREIGN.inputBg,
                    border: `1px solid ${SOVEREIGN.inputBorder}`,
                    color: SOVEREIGN.title,
                  }}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}

function EmptyState({
  hasItems,
  onClearAll,
  canCreate,
}: {
  hasItems: boolean;
  onClearAll: () => void;
  canCreate: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="relative">
        <span
          className="flex h-16 w-16 items-center justify-center rounded-2xl"
          style={{
            background: `linear-gradient(135deg, ${SOVEREIGN.cardBg} 0%, ${SOVEREIGN.paper} 100%)`,
            boxShadow: `0 0 0 1px ${SOVEREIGN.cardBorder}`,
          }}
        >
          <FolderKanban className="h-6 w-6" style={{ color: SOVEREIGN.bottle }} strokeWidth={2} />
        </span>
        <span
          aria-hidden
          className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full shadow-sm"
          style={{ background: 'rgba(201,169,97,0.18)', color: '#8A5A08', boxShadow: `0 0 0 2px ${SOVEREIGN.paper}` }}
        >
          <Sparkles className="h-2.5 w-2.5" strokeWidth={2.6} />
        </span>
      </div>
      <p className="text-[14px] font-semibold tracking-tight" style={{ color: SOVEREIGN.title }}>
        {hasItems ? 'No projects match your filters' : 'No projects assigned to your role yet'}
      </p>
      <p className="text-[12px] max-w-md" style={{ color: SOVEREIGN.kpiCaption }}>
        {hasItems
          ? 'Try clearing the search or changing the category, status, or location filters.'
          : canCreate
            ? 'Get started by creating your first project.'
            : 'Once a project is assigned to your role, it will appear here.'}
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {hasItems && (
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.title,
            }}
          >
            Clear all filters
          </button>
        )}
        {canCreate && (
          <Link
            to="/epc/projects/new"
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-white transition hover:opacity-95"
            style={{
              background: SOVEREIGN.btn,
              border: `1px solid ${SOVEREIGN.btnBorder}`,
              boxShadow: SOVEREIGN.btnShadow,
            }}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.4} />
            New project
          </Link>
        )}
      </div>
    </div>
  );
}
